import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ActressCard from '@/components/base/ActressCard';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getActressesByCategory } from '@/mocks/actresses';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const actresses = getActressesByCategory(category?.name || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600">分类未找到</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 日本女优大全`,
    description: category.description,
    url: `https://www.ribennvyou.com/category/${category.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '日本女优大全',
      url: 'https://www.ribennvyou.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - 日本女优大全 | 日本女优资料库`}
        description={category.description}
        keywords={`${category.name},日本女优,日本女优资料库,日本演艺女优`}
        canonicalUrl={`https://www.ribennvyou.com/category/${category.slug}`}
        schema={pageSchema}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  共收录 {actresses.length} 位女优
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl">{category.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">{cat.count}</span>
              </Link>
            ))}
          </div>

          {actresses.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {actresses.map((actress) => (
                <ActressCard key={actress.id} actress={actress} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-user-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类暂无女优数据</p>
              <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block">
                返回首页
              </Link>
            </div>
          )}

          {actresses.length > 0 && (
            <div className="mt-12 p-6 bg-background-100 rounded-xl">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">关于{category.name}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {category.description}本分类收录了日本演艺圈中{category.name}的详细信息，
                包括出道年份、代表作品、获奖记录以及最新动态。您可以点击任意女优卡片查看完整的个人资料页面。
                数据持续更新中，敬请关注。
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}